import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const title = 'Theming';
export const _frontmatter = {};
const layoutProps = {
  title,
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h1 {...{
      "id": "theming"
    }}>{`Theming`}</h1>
    <p>{`Rebass components are built with themeability in mind.
All colors, typographic styles, layout styles, and component variants can be completely customized with theming.`}</p>
    <h2 {...{
      "id": "themeprovider"
    }}>{`ThemeProvider`}</h2>
    <p>{`To apply themes to Rebass components, add a `}<a parentName="p" {...{
        "href": "https://www.styled-components.com/docs/advanced#theming"
      }}>{`ThemeProvider`}</a>{` component to the root of your application and pass a `}<inlineCode parentName="p">{`theme`}</inlineCode>{` object as a prop.`}</p>
    <p>{`If you're using Emotion by itself, install the `}<inlineCode parentName="p">{`emotion-theming`}</inlineCode>{` package.
If you're using Rebass with `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{`, use its `}<inlineCode parentName="p">{`ThemeProvider`}</inlineCode>{` or the `}<inlineCode parentName="p">{`gatsby-plugin-theme-ui`}</inlineCode>{` package.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import theme from './theme'
import { ThemeProvider } from 'emotion-theming'
// or for use with Theme UI:
// import { ThemeProvider } from 'theme-ui'

export default props =>
  <ThemeProvider theme={theme}>
    {props.children}
  </ThemeProvider>
`}</code></pre>
    <p>{`Rebass follows the `}<a parentName="p" {...{
        "href": "https://github.com/system-ui/theme-specification"
      }}>{`Theme Specification`}</a>{`,
which allows you to define thematic values in a more portable format.
Themes created for `}<a parentName="p" {...{
        "href": "https://github.com/jxnblk/styled-system"
      }}>{`Styled System`}</a>{` or `}<a parentName="p" {...{
        "href": "https://theme-ui.com"
      }}>{`Theme UI`}</a>{` will work with Rebass with no additional configuration required.
This also means that themes created for use with Rebass will work in other applications that follow the same specification.`}</p>
    <h2 {...{
      "id": "example"
    }}>{`Example`}</h2>
    <p>{`The following is an example theme, showing `}<em parentName="p">{`some`}</em>{` of the design constraints that can be defined as scales,
including colors, typography, layouts, and variants.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`// example theme.js
export default {
  breakpoints: ['40em', '52em', '64em'],
  fontSizes: [
    12, 14, 16, 20, 24, 32, 48, 64
  ],
  colors: {
    blue: '#07c',
    lightgray: '#f6f6ff'
  },
  space: [
    0, 4, 8, 16, 32, 64, 128, 256
  ],
  fonts: {
    body: 'system-ui, sans-serif',
    heading: 'inherit',
    monospace: 'Menlo, monospace',
  },
  fontWeights: {
    body: 400,
    heading: 700,
    bold: 700,
  },
  lineHeights: {
    body: 1.5,
    heading: 1.25,
  },
  shadows: {
    small: '0 0 4px rgba(0, 0, 0, .125)',
    large: '0 0 24px rgba(0, 0, 0, .125)'
  },
  variants: {
  },
  text: {
  },
  buttons: {
    primary: {
      color: 'white',
      bg: 'primary',
    }
  }
}
`}</code></pre>
    <h2 {...{
      "id": "preset"
    }}>{`Preset`}</h2>
    <p>{`To quickly get started without creating a custom theme, install the Rebass Preset package and use it in place of a custom theme.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-sh"
      }}>{`npm i @rebass/preset
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import { ThemeProvider } from 'emotion-theming'
import preset from '@rebass/preset'

export default props =>
  <ThemeProvider theme={preset}>
    {props.children}
  </ThemeProvider>
`}</code></pre>
    <h2 {...{
      "id": "demo"
    }}>{`Demo`}</h2>
    <p>{`Edit the theme in the demo below to see how colors, space, and typographic scales affect the Button component.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx",
        "metastring": "live=true",
        "live": "true"
      }}>{`<ThemeProvider
  theme={{
    colors: {
      background: 'black',
      primary: 'tomato',
    },
    space: [ 0, 6, 12, 24, 48 ],
    fontSizes: [ 14, 16, 18, 20, 24 ],
    radii: {
      default: 12,
    }
  }}>
  <Button>Beep</Button>
</ThemeProvider>
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      